/**
 * core-utils.js
 *
 * This is a small collection of helper methods meant to support scripts
 * loaded in the head, such as experiments.js.
 */
(function(win) {

  // SlideShare's global object.
  var SS_NAMESPACE = 'slideshare_object',

      MODULES_ROOT = SS_NAMESPACE + '._modules.',

      I18N_ROOT = SS_NAMESPACE + '._i18n.',

      I18N_LOCALE = false,
      DEFAULT_LANG = 'en',

      // A regular expression to match non-valid CSS selector names.
      REGEX_INVALID_CSS_NAME = /[^a-zA-Z\d\-_]/g,

      // A reference to the toString prototype method for type checking.
      objToString = Object.prototype.toString,

      // The toString output for type matching strings.
      STRING_TAG = '[object String]',

      // The toString output for type matching numbers.
      NUMBER_TAG = '[object Number]',

      // The toString output for type matching arrays.
      ARRAY_TAG = '[object Array]',

      // The toString output for type matching objects.
      OBJECT_TAG = '[object Object]',

      I18N_TAG = 'meta[name="ss-i18n-translations"]',

      // A collection of utility methods.
      utils = {},

      localesMap = {
        en: 'en_US',
        fr: 'fr_FR',
        es: 'es_ES',
        pt: 'pt_BR',
        de: 'de_DE'
      };

  /**
   * Validates whether the given value is of the given type as outputted by Object.prototype.toString.call.
   * @param {*} value The value to validate.
   * @param {string} typeTag The type to validate against.
   * @returns {boolean} Is the given value of the given type?
   */
  function matchesType(value, typeTag) {
    return objToString.call(value) === typeTag;
  }

  /**
   * The internal component to a page-level "module" loading system where objects can be stored
   * and retrieved from within a container on our global object. This is the basis for convenience methods
   * for organizing objects without polluting the window.
   * @param {string} path The path referencing an object ('get' mode or 'set' mode when passed with a module).
   * @param {*} [module] The object you wish to store ('set' mode).
   * @returns {*} The resolved object ('get' mode).
   */
  function getOrSetModule(path, module) {

    var segments = path.split('.'),
      len = segments.length,
      memo = win,
      s = 0,
      segment;

    for (s; s < len; s++) {
      segment = segments[s];
      if (!memo[segment]) {
        if (!module) {
          return null;
        }
        memo[segment] = s + 1 === len ? module : {};
      }
      memo = memo[segment];
    }

    return memo;
  }

  /**
   * Loads translated i18n strings from meta tags generated using the
   * i18n_to_meta Rails helper.
   */
  function loadI18n() {

    // Gather all the i18n meta tags on the document.
    var tags = win.document.querySelectorAll(I18N_TAG);

    utils.i18n_locale();

    // Iterate over each tag and extract the content as JSON.
    Array.prototype.slice.call(tags).forEach(function(tag) {
      var translations = JSON.parse(tag.content);

      // Register each i18n key.
      Object.keys(translations).forEach(function(key) {
        getOrSetModule(I18N_ROOT + key, translations[key]);
      });
    });
  }

  utils.i18n_locale = function(){
    if(!I18N_LOCALE){
      var lang = document.documentElement.lang || DEFAULT_LANG;
      I18N_LOCALE = localesMap[lang] || localesMap[DEFAULT_LANG];
    }
    return I18N_LOCALE;
  };

  /**
   * A quick check to validate whether or not the value is a string.
   * @param value The value to validate.
   * @returns {boolean} Is the value a string?
   */
  utils.isString = function(value) {
    return matchesType(value, STRING_TAG);
  };

  /**
   * A quick check to validate whether or not the value is a number.
   * @param value The value to validate.
   * @returns {boolean} Is the value a number?
   */
  utils.isNumber = function(value) {
    return matchesType(value, NUMBER_TAG);
  };

  /**
   * A quick check to validate whether or not the value is an array.
   * @param value The value to validate.
   * @returns {boolean} Is the value an array?
   */
  utils.isArray = function(value) {
    return matchesType(value, ARRAY_TAG);
  };

  /**
   * A quick check to validate whether or not the value is a plain old JavaScript object.
   * Note that this method is meant for quick and dirty checks and is not nearly as robust
   * as it's jQuery/underscore/lodash counterparts. You should use one of those if available.
   * @param value The value to validate.
   * @returns {boolean} Is the value a plain old JavaScript object?
   */
  utils.isPlainObject = function(value) {
    return matchesType(value, OBJECT_TAG);
  };

  /**
   * Sets a given class on a given element which can be a raw DOM node or a selector.
   * @param {HTMLElement|string} el A reference to the element to update.
   * @param {string} className The class to append.
   * @returns {HTMLElement} The passed element.
   */
  utils.addClass = function(el, className) {

    // If el is an element, we're good. Otherwise, assume it's a selector and select it.
    el = (el && el.nodeType === 1) ? el : document.querySelector(el);

    // If an element hasn't been identified, exit.
    if (!el) {
      return null;
    }

    // Set the class if it's not already set.
    if (!el.className) {
      el.className = className;
    }
    else if (!(new RegExp('\\b' + className + '\\b')).test(el.className)) {
      el.className += ' ' + className;
    }

    return el;
  };

  /**
   * A little helper method for transforming strings into proper CSS selector names.
   * Only alphanumeric, hyphen, and underscores will be considered valid here.
   * All other characters will be converted to hyphens.
   * @param {string} string The string you wish to sanitize. Most likely a CSS class name.
   * @returns {string} The sanitized string.
   * @private
   */
  utils.sanitizeForCSS = function(string) {
    return string.replace(REGEX_INVALID_CSS_NAME, '-');
  };

  /**
   * Here are all the conditions I know of that must pass to determine if a user
   * is authenticated on the front end.
   * @returns {boolean}
   */
  utils.isUserAuthenticated = function() {
    var user = win[SS_NAMESPACE].user;
    return user.member_type !== 'non-member' && user.loggedin !== false && user.login !== 'guest';
  };

  /**
   * Checks if the window width is below responsive threshold
   * @returns {boolean}
   */
   utils.isResponsive = function() {
    return window.innerWidth < 928
  };

  /**
   * Retrieves translated i18n strings by key. On the first invocation, translations
   * will loaded from meta tags generated by the i18n_to_meta Rails helper.
   *
   * Retrieve one key value:
   * utils.i18n('clipping.clipboard');
   *  -> 'Clipboard'
   *
   * Retrieve all the set key values:
   * utils.i18n();
   *  -> {clipping: {clipboard: 'Clipboard', create: 'Create a clipboard'}}
   *
   * You can use XMessage and interpolate strings by specifing the values in config parameter:
   * utils.i18n('clipboard.count', { count: 1 });
   *
   * @param {string} [key] The i18n key you wish to retrieve.
   * @param {object} [config] Object, primitive or array of objects/primitives
   * @returns {*} Either an i18n value, a value map, or undefined if the key is not found.
   */
  utils.i18n = function(key, config) {
    if (!getOrSetModule(I18N_ROOT)) {
      loadI18n();
    }
    if (key) {
      var translatedString = getOrSetModule(I18N_ROOT + key);

      // Interpolate XMessage string if XMessage library has been loaded
      if (translatedString && config && window.xmessage) {
        var messageFn = window.xmessage.fromString(translatedString, I18N_LOCALE);

        // We authorize config parameter to not be an array so we need to create one in that case
        if (!Array.isArray(config)) {
          config = [config];
        }

        translatedString = messageFn(config);
      }

      return translatedString;
    }
    return getOrSetModule(I18N_ROOT.slice(0, -1));
  };

  /**
   * Sets an object at a specific location for easy retrieval elsewhere. Use dot separation
   * for namespacing the objects if you like. E.g.:
   *    exports('Pizza', pizza);
   *    exports('foods.favorites.Pizza', pizza);
   * @param {string} path The location you wish to store your object. Use dots for namespacing.
   * @param {*} module The object to store.
   */
  utils.exports = function(path, module) {
    getOrSetModule(MODULES_ROOT + path, module);
  };

  /**
   * Retrieves an object that was stored using the 'exports' method.
   * @param {string} path The location of the object you wish to retrieve.
   * @returns {*} The resolved object.
   */
  utils.imports = function(path) {
    return getOrSetModule(MODULES_ROOT + path);
  };

  // Set the utilities object and create namespace objects needed for storing modules.
  getOrSetModule(SS_NAMESPACE + '.utils', utils);

}(window));
